import { api } from './api';
import type { DeleteResponse, PaginatedResponse } from '../types';

// API Response wrapper type
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// Types
export interface Ministry {
  _id: string;
  name: string;
  logoPath?: string;
  appId?: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  isDelete?: boolean;
  isDeleted?: boolean;
}

export interface MinistryCreateRequest {
  name: string;
  logoPath?: string;
  appId?: string;
}

export interface MinistryUpdateRequest {
  name?: string;
  logoPath?: string;
}

export interface MinistryQueryParams {
  include_deleted?: boolean;
  is_active?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  /** Per-column filters as a JSON string: {"field":{"value":..,"matchMode":".."}} */
  filters?: string;
  app_id?: string;   // Filter to one accessible project (intersected server-side)
  include_global?: boolean;   // Also include core/global rows (appId=null)
}

/** Re-scope preview: which projects are aligned to a ministry. */
export interface RescopePreview {
  canRescope: boolean;
  conflicts: Record<string, { departments: number; unitTypes: number; units: number; personnel: number; users: number }>;
  references: Record<string, { departments: number; unitTypes: number; units: number; personnel: number; users: number }>;
}

export const ministriesService = {
  // Get all ministries
  getAll: async (params?: MinistryQueryParams): Promise<PaginatedResponse<Ministry>> => {
    const response = await api.get<PaginatedResponse<Ministry>>('/api/v1/ministries/list', { params });
    return response.data;
  },

  // Preview re-scoping a ministry to a project (platform admin only).
  getRescopePreview: async (id: string, appId?: string | null): Promise<RescopePreview> => {
    const response = await api.get<{ data: RescopePreview }>(
      `/api/v1/ministries/${id}/rescope-preview`,
      { params: { app_id: appId || undefined } }
    );
    return response.data.data;
  },

  // Change a ministry's owning project (null/empty = global). Platform admin only.
  rescopeProject: async (id: string, appId: string | null): Promise<Ministry> => {
    const response = await api.patch<{ data: Ministry }>(
      `/api/v1/ministries/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  // Get all ministries for dropdown (no pagination, fetches all records)
  getAllForDropdown: async (): Promise<Ministry[]> => {
    const response = await api.get<PaginatedResponse<Ministry>>('/api/v1/ministries/list', {
      params: { include_deleted: false },
    });
    return response.data.data || [];
  },

  // Get ministry by ID
  getById: async (id: string): Promise<Ministry> => {
    const response = await api.get<ApiResponse<Ministry>>(`/api/v1/ministries/get/${id}`);
    return response.data.data;
  },

  // Create new ministry
  create: async (payload: MinistryCreateRequest): Promise<Ministry> => {
    const response = await api.post<ApiResponse<Ministry>>('/api/v1/ministries/create', payload);
    return response.data.data;
  },

  // Update ministry
  update: async (id: string, payload: MinistryUpdateRequest): Promise<Ministry> => {
    const response = await api.put<ApiResponse<Ministry>>(`/api/v1/ministries/update/${id}`, payload);
    return response.data.data;
  },

  // Delete ministry (soft delete)
  delete: async (id: string): Promise<DeleteResponse> => {
    const response = await api.delete<ApiResponse<DeleteResponse>>(`/api/v1/ministries/delete/${id}`);
    return response.data.data;
  },

  // Restore deleted ministry
  restore: async (id: string): Promise<Ministry> => {
    const response = await api.patch<ApiResponse<Ministry>>(`/api/v1/ministries/restore/${id}`);
    return response.data.data;
  },
};
